import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import moment from 'moment';

import type { BoardPreviewMap, ImageCropData, RecordData, Service } from 'webapp-types';
import {
	ModelName,
	NewReportSlideLayout,
	ReportLayout,
	ReportSlideFragmentType,
} from 'webapp/enums/enums';
import { compact, sortBy, uniq } from 'webapp/utils/common';
import { getFormattedTime } from 'webapp/utils/number';
import { isVerified, type VerificationInfo } from 'webapp/utils/verification';

import BufferedModel from 'webapp/models-custom/buffer/buffered-model';
import type Reminder from 'webapp/models-custom/reminder';
import type ReportScheduleSetting from 'webapp/models-custom/report-schedule-setting';
import type ReportSlide from 'webapp/models-custom/report-slide';
import type ReportSlideFragment from 'webapp/models-custom/report-slide-fragment';
import type Template from 'webapp/models-custom/template';
import type ReportRecord from 'webapp/models/report';
import type ReportSlideFragmentRecord from 'webapp/models/report-slide-fragment';
import type TemplateRecord from 'webapp/models/template';
import type UserRecord from 'webapp/models/user';

import { Severity } from 'webapp/services/logger';

export const DEFAULT_REPORT_TITLE = 'New report title';

export default class Report extends BufferedModel<ReportRecord, RecordData.Report.Serialized> {
	@service('account') declare accountService: Service.Account;
	@service('logger') declare loggerService: Service.Logger;
	@service('request') declare requestService: Service.Request;
	@service declare infolayer: Service.Infolayer;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('model-manager/report-slides')
	declare mmReportSlidesService: Service.ModelManager.ReportSlides;
	@service('model-manager/report-schedule-settings')
	declare mmReportScheduleSettingsService: Service.ModelManager.ReportScheduleSettings;
	@service('model-manager/reminders') declare mmRemindersService: Service.ModelManager.Reminders;
	@service('model-manager/templates') declare mmTemplatesService: Service.ModelManager.Templates;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('model-manager/tags') declare mmTagsService: Service.ModelManager.Tags;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/report-slide-fragments')
	declare mmReportSlideFragmentsService: Service.ModelManager.ReportSlideFragments;

	@tracked override animationClass = ''; // used for animations in lazy-loading

	get addNewSlideLayoutsDisabled(): NewReportSlideLayout[] {
		const disabledNewSlideLayoutsForCurrentPlan =
				this.mmReportSlidesService.disabledNewSlideLayoutsForCurrentPlan,
			baseSlideTypes = [
				NewReportSlideLayout.COVER,
				NewReportSlideLayout.BOARD,
				NewReportSlideLayout.COLUMNS_1,
				NewReportSlideLayout.COLUMNS_2,
				NewReportSlideLayout.COLUMNS_3,
				NewReportSlideLayout.DATA_STORY,
			],
			slideTypesDisabled = baseSlideTypes.filter((slideType) => {
				return (
					!!disabledNewSlideLayoutsForCurrentPlan[slideType] ||
					(slideType === NewReportSlideLayout.BOARD &&
						!this.slidesTypeBoardRemainingCount)
				);
			});

		return slideTypesDisabled;
	}

	// ------- props from / to ED record ------

	get id(): string | null {
		return this.model.id;
	}

	get title(): string {
		return this.getBuffered('title');
	}

	set title(value: string) {
		this.setBuffered('title', value);
	}

	get verificationInfo(): VerificationInfo | undefined {
		return this.model.verificationInfo;
	}

	get isVerified(): boolean {
		return isVerified(this.verificationInfo);
	}

	get color(): string {
		return this.model.color;
	}

	get image(): string {
		return this.model.image;
	}

	get slideCount(): number {
		return this.model.slideCount;
	}

	get themeId(): number {
		return this.model.themeId;
	}

	get ownerId(): number | null {
		return this.getBuffered('ownerId');
	}

	set ownerId(value: number | null) {
		this.setBuffered('ownerId', value);
	}

	get created(): number {
		return this.model.created;
	}

	get createdBy(): number {
		return this.model.createdBy;
	}

	get modified(): number | null {
		return this.model.modified;
	}

	get order(): number {
		return this.getBuffered('order');
	}

	set order(value: number) {
		this.setBuffered('order', value);
	}

	get favorite(): boolean {
		return this.getBuffered('favorite');
	}

	set favorite(value: boolean) {
		this.setBuffered('favorite', value);
	}

	get tags(): number[] {
		return this.getBuffered('tags');
	}

	set tags(value: number[]) {
		this.setBuffered('tags', value);
	}

	get templateId(): number {
		return this.model.templateId;
	}

	get spaceId(): number {
		return this.model.spaceId;
	}

	get originateFrom(): number | null {
		return this.model.originateFrom;
	}

	get token(): string {
		return this.model.token;
	}

	get isPublicEnabled(): boolean {
		return this.getBuffered('isPublicEnabled');
	}

	set isPublicEnabled(value: boolean) {
		this.setBuffered('isPublicEnabled', value);
	}

	get restrictIps(): string[] {
		return this.getBuffered('restrictIps');
	}

	set restrictIps(value: string[]) {
		this.setBuffered('restrictIps', value);
	}

	get password(): string {
		return this.getBuffered('password');
	}

	set password(value: string) {
		this.setBuffered('password', value);
	}

	get allowAnonymousDateRangeEdit(): boolean {
		return this.getBuffered('allowAnonymousDateRangeEdit');
	}

	set allowAnonymousDateRangeEdit(value: boolean) {
		this.setBuffered('allowAnonymousDateRangeEdit', value);
	}

	get allowAnonymousFilterEdit(): boolean {
		return this.getBuffered('allowAnonymousFilterEdit');
	}

	set allowAnonymousFilterEdit(value: boolean) {
		this.setBuffered('allowAnonymousFilterEdit', value);
	}

	get deleted(): number | null {
		return this.getBuffered('deleted');
	}

	set deleted(value: number | null) {
		this.setBuffered('deleted', value);
	}

	get cover(): string | null | undefined {
		return this.getBuffered('cover');
	}

	set cover(value: string | null) {
		this.setBuffered('cover', value);
	}

	get coverOriginal(): string | null | undefined {
		return this.getBuffered('coverOriginal');
	}

	set coverOriginal(value: string | null) {
		this.setBuffered('coverOriginal', value);
	}

	get coverCrop(): ImageCropData | null | undefined {
		return this.getBuffered('coverCrop');
	}

	set coverCrop(value: ImageCropData | null) {
		this.setBuffered('coverCrop', value);
	}

	get layout(): ReportLayout {
		return this.getBuffered('layout');
	}

	set layout(value: ReportLayout) {
		this.setBuffered('layout', value);
	}

	get isSample(): boolean {
		return this.getBuffered('isSample');
	}

	set isSample(value: boolean) {
		this.setBuffered('isSample', value);
	}

	// ------- other props ------

	get isForCurrentSpace(): boolean {
		return !this.spaceId || this.spaceId === this.accountService.id;
	}

	get isMine(): boolean {
		return this.createdBy === this.currentUserService.numericId;
	}

	get name() {
		return this.title; // for filter-sort compatibility
	}

	@cached
	get canCurrentUserEditWithoutDeleted(): boolean {
		// is creator or isnt permission level user without deleted
		return (
			(!this.isPublicTemplate || this.accountService.isPublictemplatesSpace) &&
			!this.currentUserService.isViewer &&
			(this.isMine || !this.currentUserService.isUser) &&
			(!this.spaceId || this.spaceId === this.accountService.id)
		);
	}

	@cached
	get canCurrentUserEdit(): boolean {
		// is creator or isnt permission level user
		return this.canCurrentUserEditWithoutDeleted && !this.deleted;
	}

	get createdText(): string {
		return getFormattedTime(this.created, this.currentUserService.model?.timezone);
	}

	get createdByUser(): UserRecord | null {
		let user;
		if (this.createdBy) {
			user = this.mmUsersService.findRecord(String(this.createdBy));
			if (!user) {
				this.loggerService.logError(
					'Missing createdBy for report',
					{
						reportId: this.id,
						userId: this.createdBy,
					},
					Severity.WARNING,
				);
			}
		}
		return user || null;
	}

	get modifiedText(): string {
		if (this.modified) {
			return getFormattedTime(
				this.modified,
				this.currentUserService.model?.timezone,
				this.accountService.accountDatetimeFormatHumanReadable,
			);
		} else {
			return '';
		}
	}

	get ownerUser(): UserRecord | null {
		let user;
		if (this.ownerId) {
			user = this.mmUsersService.findRecord(String(this.ownerId));
			// if (!user) {
			// 	this.loggerService.logError('Missing owner for report', {
			// 		reportId: this.id,
			// 		userId: this.ownerId
			// 	}, Severity.WARNING);
			// }
		}
		return user || null;
	}

	get internalUrl(): string {
		return this.accountService.appUrl + '/reports/view/' + this.id;
	}

	@cached
	get tagsObjs() {
		return this.mmTagsService.savedRecords.filter((tag) => {
			return this.tags.includes(+tag.id!);
		});
	}

	get tagsText(): string {
		return this.tagsObjs.map((item) => item.name).join(', ') || '';
	}

	get favoriteText(): string {
		return this.favorite ? 'Unfavorite' : 'Favorite';
	}

	_slides: ReportSlide[] = [];

	get slides(): ReportSlide[] {
		const slideRecords = this.mmReportSlidesService.getRecordsForReport(this.numericId!);
		this.mmReportSlidesService.updateModelCache(this._slides, slideRecords, (model) => {
			return this.mmReportSlidesService.createModel(model);
		});
		return this._slides;
	}

	get boardSlides(): ReportSlide[] {
		return this.slides.filter((slide) => slide.isTypeBoard);
	}

	get boardFragments(): ReportSlideFragment[] {
		return compact(this.boardSlides.flatMap((slide) => slide.boardFragment));
	}

	get slidesTypeNotBoard(): ReportSlide[] {
		return this.slides.filter((item) => !item.isTypeBoard);
	}

	get hasSlidesBoardOrBlockFragment(): boolean {
		return this.slides.some((slide) => {
			const types = slide.fragments.map((item) => item.type);

			return (
				types.includes(ReportSlideFragmentType.BOARD) ||
				types.includes(ReportSlideFragmentType.BLOCK)
			);
		});
	}

	get sortedSlides(): ReportSlide[] {
		return sortBy(this.slides, 'order', 'id');
	}

	get slideCountText(): string {
		const slideCount = this.slideCount;

		return `${slideCount} slide${slideCount > 1 ? 's' : ''}`;
	}

	get sortedSlidesIds(): number[] {
		return this.sortedSlides.map((slide) => slide.numericId!) ?? [];
	}

	get firstSlide() {
		return this.sortedSlides[0] || null;
	}

	get isFirstSlideBoard() {
		return this.firstSlide?.isTypeBoard;
	}

	@cached
	get slidesTypeBoardRemainingCount(): number {
		return Math.max(
			0,
			Report.maxSlidesTypeBoard - (this.slides.length - this.slidesTypeNotBoard.length),
		);
	}

	_fragments: ReportSlideFragment[] = [];

	@cached
	get fragments(): ReportSlideFragment[] {
		const fragmentRecords: ReportSlideFragmentRecord[] = [];
		this.slides.forEach((slide) => {
			fragmentRecords.push(
				...this.mmReportSlideFragmentsService.getRecordsForSlide(slide.numericId!),
			);
		});

		this.mmReportSlideFragmentsService.updateModelCache(
			this._fragments,
			fragmentRecords,
			this.mmReportSlideFragmentsService.createModel,
		);
		return this._fragments;
	}

	get maxOrder(): number {
		return Math.max.apply(
			Math.max,
			this.slides.map((item) => item.order),
		);
	}

	get shownLogo(): string {
		return this.accountService.boardLogo;
	}

	get fallbackImage(): string {
		return this.isFirstSlideBoard
			? ''
			: this.mmReportSlidesService.getSlideFallbackImage(this.bgTheme);
	}

	get bgTheme(): string {
		return this.themeId ? this.mmBoardsService.getBgTheme(this.color, this.themeId) : '';
	}

	get allImages() {
		const images: string[] = [];
		this.slides.forEach((slide) => {
			images.push(...slide.allImages);
		});
		return uniq(images);
	}

	/* Currently unused because of optimization
	get sourceTypeKeys(): string[] {}
	*/

	get scheduleSettings(): ReportScheduleSetting[] {
		return this.numericId
			? this.mmReportScheduleSettingsService.getForReport(this.numericId)
			: [];
	}

	get automatedScheduleSettings(): ReportScheduleSetting[] {
		return this.numericId
			? this.mmReportScheduleSettingsService.getAutomatedForReport(this.numericId)
			: [];
	}

	get oneOffScheduleSettings(): ReportScheduleSetting[] {
		// only the ones not triggered yet
		const allSettings = this.scheduleSettings;
		return allSettings.filter((s) => s.oneOffTimeLeft > 0);
	}

	get reminders(): Reminder[] {
		return this.numericId ? this.mmRemindersService.getForReport(this.numericId) : [];
	}

	get isColorAppliedToAllSlidesTypeNotBoard(): boolean {
		return this.slidesTypeNotBoard.every(
			(slide) =>
				slide.color !== this.mmReportSlidesService.defaultReportSlideColor &&
				slide.color === this.slidesTypeNotBoard[0]?.color,
		);
	}

	get isLogoAppliedToAllSlidesTypeNotBoard(): boolean {
		return this.slidesTypeNotBoard.every(
			(slide) => (slide.logo || '') === (this.slidesTypeNotBoard[0]?.logo || ''),
		);
	}

	get isBackgroundAppliedToAllSlidesTypeNotBoard(): boolean {
		return this.slidesTypeNotBoard.every(
			(slide) => (slide.bg || '') === (this.slidesTypeNotBoard[0]?.bg || ''),
		);
	}

	get isBackgroundOpacityAppliedToAllSlidesTypeNotBoard(): boolean {
		const defaultBgImageOpacity = this.mmBoardsService.defaultBgImageOpacity;
		return this.slidesTypeNotBoard.every(
			(slide) =>
				(slide.bgOpacity || defaultBgImageOpacity) ===
				(this.slidesTypeNotBoard[0]?.bgOpacity || defaultBgImageOpacity),
		);
	}

	get isVisualSettingsAppliedToAllSlides(): boolean {
		return (
			this.isColorAppliedToAllSlidesTypeNotBoard &&
			this.isLogoAppliedToAllSlidesTypeNotBoard &&
			this.isBackgroundAppliedToAllSlidesTypeNotBoard &&
			this.isBackgroundOpacityAppliedToAllSlidesTypeNotBoard
		);
	}

	get datawallUrl(): string {
		return this.accountService.appUrl + '/datawall/' + this.token;
	}

	getInternalUrlWithPreviewHash(_previewHash: BoardPreviewMap = {}, _isIframe = false): string {
		return this.mmBoardsService.getUrlWithPreviewHash(this.internalUrl);
	}

	get isEmpty(): boolean {
		if (this.slides.length === 0) {
			return true;
		}

		if (this.slides.length === 1) {
			const firstSlide = this.firstSlide!;
			return firstSlide.fragments.every((fragment) => {
				return fragment.isEmpty;
			});
		}

		return false;
	}

	get isPageless(): boolean {
		return this.layout === ReportLayout.PAGELESS;
	}

	get shownFragments(): ReportSlideFragment[] {
		const fragments = this.fragments;

		if (!this.isPageless) {
			return sortBy(fragments, 'order');
		}

		const firstSlide = this.firstSlide;
		let filteredFragments = fragments;
		// cover image fragment (order 11) is not shown on pageless
		if (firstSlide?.isTypeCoverPageless) {
			const coverImageFragment = firstSlide.fragments.find(
				(fragment) => fragment.isTypeImage,
			);

			if (coverImageFragment) {
				filteredFragments = fragments.filter((f) => f.id !== coverImageFragment.id);
			}
		}

		return sortBy(filteredFragments, 'order');
	}

	// ----------- Template stuff -----------

	get templateRecord(): TemplateRecord | null {
		const templateId = this.templateId;
		let record;
		if (templateId) {
			record = this.mmTemplatesService.findRecord(String(templateId));
		}
		return record || null;
	}

	_templateModel: Template | null = null;

	@cached
	get templateModel(): Template | null {
		const model = this.templateRecord;

		if (model && model?.id !== this._templateModel?.id) {
			this._templateModel = this.mmTemplatesService.createModel(model);
		}

		return this._templateModel;
	}

	get isTemplate(): boolean {
		return !!this.templateRecord;
	}

	get isPublicTemplate(): boolean {
		return !!this.templateRecord?.isPublic;
	}

	get isAccountTemplate(): boolean {
		return this.isTemplate && !this.templateRecord?.isPublic;
	}

	// ----------- Methods -----------

	getSlideWithGlobalRowIndex(globalRowIndex: number) {
		// use existing fragment in this row as reference
		const fragments = this.fragments;
		const findFragmentInRow = (rowIndex: number) => {
			return fragments.find((f) => f.globalPosition.row === rowIndex);
		};
		const referenceFragment =
			findFragmentInRow(globalRowIndex) ||
			findFragmentInRow(globalRowIndex - 1) ||
			findFragmentInRow(globalRowIndex + 1) ||
			fragments[0];
		return referenceFragment?.slide || this.slides[0];
	}

	getDatawallUrlWithPreviewHash(previewHash: BoardPreviewMap = {}, isIframe = false): string {
		return this.mmBoardsService.getUrlWithPreviewHash(this.datawallUrl, previewHash, isIframe);
	}

	fetchBoardPreviews(): Promise<boolean[]> {
		return Promise.all(this.boardFragments.map((fragment) => fragment.requestBoardPreview()));
	}

	override delete(skipBE = false): Promise<unknown> {
		let promise;

		// to handle soft delete
		// when skipBE is true and deleteRecord/ApplicationSerializer is used the model stays in storage marked as "isDeleted"
		// because of this later push of the same soft deleted report with deleted property is not pushed to store
		if (!this.deleted) {
			this.directPropUpdate({ deleted: moment().unix() });

			promise = Promise.resolve();
		} else {
			promise = super.delete(skipBE);
		}

		return promise;
	}

	cleanup(): Promise<unknown> {
		const deletePromise = this.requestService
			.api('DELETE', `/reports/${this.id}?permanent=true`)
			.then(() => {
				// skip backend call since we already called the endpoint
				return super.delete(true);
			});

		return deletePromise.catch(
			this.requestService.showAllValidationErrors.bind(this.requestService),
		);
	}

	// ----------- Static -----------

	static maxSlidesTypeBoard = 40;

	static override modelName = ModelName.REPORT; // store record type
}
